import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { TitleCasePipe } from "@angular/common";

import { FormBuilder, FormGroup, Validators, AbstractControl, FormControl } from "@angular/forms";
import { Subscription, BehaviorSubject, Observable, from, of } from "rxjs";
import { tap, map, mergeMap, catchError, finalize, switchMap } from "rxjs/operators";
import { WorkflowData, ApplicationState, ApplicationSteps, WorkflowFieldData, WorkflowFieldFile, PaymentSteps, ApplicationPaymentParameters, ApplicationType, ApplicationStateChangeRequest } from "../../../models/applications.models";
import { LoaderService } from "../../../../common/services/loader.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { Organization } from "../../../../common/models/organization.model";
import { ApplicationBaseComponent } from "../../application-base.component";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { CasingTypeModel, FormationModel, DrillingEquipmentModel } from "../../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { ApplicationsService } from "../../../services/applications.service";
import { AuthService } from "../../../../services/auth.service";
import { PaymentService, AuthorizationRequest } from "../../../../payments/services/payment.service";
import { PaymentsComponent } from "../../payments/payments.component";
import { ApplicationPayment, Payment, ApplicationCreditAuthorization, AuthorizationResponse } from "../../../../payments/models/paymentItem.model";
import { ApplicationConfigService, ApplicationsConfig, ModuleConfig, ExpeditingConfig } from "../../../../services/application-config.service";
import { htmlEscape } from "../../../../Helpers/UtilityFunctions";
import { SidePanelService } from "../../../../common/services/side-panel.service";
import { ApplicationNotesService } from "../../../services/application-notes.service";
import { SystemSetting, SystemSettingSearchSpec, SettingNames } from "../../../../admin/models/system-setting.model";
import { SystemSettingsService } from "../../../../admin/services/system-settings.service";
import { LunaClaimValues } from '../../../../models/user-identity.model';
import { OnBaseDocumentTypeProcessReference } from '../../../../common/models/onBaseIntegrationAndMapping.model';


@Component({
    selector: 'app-stratigraphic-application',
    templateUrl: './stratigraphic-application.component.html',
    styleUrls: ['../../applications-home/applications-home.component.scss', './stratigraphic-application.component.scss'],
    standalone: false
})
export class StratigraphicApplicationComponent extends ApplicationBaseComponent implements OnInit, OnDestroy {

    @ViewChild(PaymentsComponent) _payments: PaymentsComponent;

    get aerialPhotographyCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get('documentation').get("aerialPhotography");
    }

    get aerialPhotographyWithContoursCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get('documentation').get("aerialPhotographyWithContours");
    }

    get urbanAreaCtrl(): AbstractControl {
        if (this.form == null) return null;;
        return <AbstractControl>this.form.get('documentation').get("urbanAreaSwornStatementAffidavit");
    }

    agencies: Array<any> = [];

    alwaysDisabledFormNames: string[] = ['organization', 'payment'];
    alwaysDisabledFieldNames: string[] = [ 'wellInformation|section', 'wellInformation|lot', 'wellInformation|tract', 'wellInformation|civilTownship', 'wellInformation|civilTownshipNumber', 'wellInformation|countyName', 'wellInformation|countyNumber', 'documentation|urbanizedArea'];

    private _applicationPaymentParameters: ApplicationPaymentParameters;

    get applicationPaymentParameters(): ApplicationPaymentParameters {
        return this._applicationPaymentParameters;
    }

    casingTypes: CasingTypeModel[] = [];


    private _config: ApplicationsConfig;

    get config(): ApplicationsConfig {
        return this._config;
	}

    onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];

	expediteSetting: SystemSetting;

	expediteConfig: ExpeditingConfig;



    get fees(): ApplicationPayment[] {
        if (this.form == null) return [];
        let group = this.form.get('payment');
        if (group == null) return [];
        let ctrl = group.get('payments');
        if (ctrl == null) return [];
        return ctrl.value;
    }

    set fees(value: ApplicationPayment[]) {
        if (this.form == null) return;
        let group = this.form.get('payment');
        if (group == null) return;
        let ctrl = group.get('payments');
        if (ctrl == null) return;
        ctrl.setValue(value);
    }
    drillingEquipment: DrillingEquipmentModel[];
    formations: FormationModel[];



    get latitude(): number | null {
        if (this.form == null) return null;
        let subForm = this.form.get('waterSource');
        if (subForm == null) return null;
        let ctrl = subForm.get('latitude');
        if (ctrl == null) return null;
        return (ctrl.value != null)
            ? <number>ctrl.value
            : null;
    }

    get longitude(): number | null {
        if (this.form == null) return null;
        let subForm = this.form.get('waterSource');
        if (subForm == null) return null;
        let ctrl = subForm.get('longitude');
        if (ctrl == null) return null;
        return (ctrl.value != null)
            ? <number>ctrl.value
            : null;
    }

   
    notesIneligibleStates: ApplicationState[] = [ApplicationState.Input, ApplicationState.Start];

    organization: Organization;

    paymentSteps = PaymentSteps;

    get requireWatershedValidation(): boolean {
        return this.latitude != null && this.longitude != null;
    }


    steps = ApplicationSteps;

    get subheaderText(): string {
        return "Application # [XXX]";
    }
    
    subheader: SubHeader = new SubHeader({ title: "", buttons: [{ name: "Save", method: () => this.save(true) }, { name: "Previous Section", method: () => this.previous() }, { name: "Next Section", method: () => this.next() }] });

    get submitButtonText(): string {
        return (this.application == null || this.application.isReturned == false)
            ? "Submit Application"
            : "Resubmit Application";
    }

    private validStringCasingTypeCodes = ['DP*', 'COND', 'MS1', 'SURF', 'I1', 'I2', 'PROD', 'PROD2', 'HOL1', 'L1']

    get withdrawButtonText(): string {
        return (this.application == null || this.application.isReturned == false)
            ? "Withdraw Application"
            : "Cancel Application";
    }

    get disableSubmission(): boolean {
        if (this.auth.User != null) {
            if (this.auth.User.systemPermissions.includes(LunaClaimValues.SubmitStratigraphicWellApplication)) {
                return false;
            };
        }
        return true;
    }

    confirmSubmitText: string = "Application #[APPNUMBER] will be submitted for review.  To withdraw a submitted application, you will have to contact ODNR personnel to have the application returned to you.  Do you wish to continue?";
    confirmSubmitDialog: Dialog = new Dialog("", [new DialogButton("NO", false, () => { return; }), new DialogButton("Yes", true, () => { this.submit(); })]);
    confirmWithdrawDialog: Dialog = new Dialog("", [new DialogButton("NO", false, () => { return; }), new DialogButton("Yes", true, () => { this.withdraw(); })]);
    invalid: Dialog = new Dialog("Your form has missing / incomplete fields and cannot be submitted.  Please review your form for accuracy and try again.", [new DialogButton("OK", false, () => { this.resetInvalid(); })]);
    submittedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been submitted for review.", [new DialogButton("OK", true, () => { this.home(); })]);
    withdrawDialog: Dialog = new Dialog("Application #[APPNUMBER] has been [STATE]", [new DialogButton("OK", true, () => { this.home(); })]);
    withdrawDialogText: string = "You are requesting Application #[APPNUMBER] to be [STATE].  This process is irreversible; you will have to start a new application if you change your mind.  Do you wish to continue?";

    constructor(private route: ActivatedRoute, loader: LoaderService, dialog: DynamicDialogService, _subheader: SubHeaderService, private builder: FormBuilder, private casePipe: TitleCasePipe,
        private domainService: DomainDefinitionService, appService: ApplicationsService, _auth: AuthService, _router: Router, private paymentService: PaymentService,
        private configService: ApplicationConfigService, _sidePanelService: SidePanelService, _notesService: ApplicationNotesService, private _settings: SystemSettingsService) {
        super(loader, dialog, _subheader, _auth, appService, _router, _sidePanelService, _notesService);
        this.numericFields = ['applicationStep', 'wellInformation|surfaceLatitude', 'wellInformation|surfaceLongitude', 'wellInformation|targetLatitude', 'wellInformation|targetLongitude', 'wellInformation|civilTownshipNumber', 'wellInformation|surfaceElevation', 'wellInformation|proposedTotalDepth', 'waterSource|latitude', 'waterSource|longitude', 'waterSource|withdrawalRate', 'waterSource|totalVolume', 'waterSource|recycledWater', 'access|latitude', 'access|longitude'];
        this.booleanFields = ['wellInformation|surfaceRights'];
        this.fileFields = ['documentation|restorationPlan', 'documentation|plat', 'documentation|aerialPhotography', 'documentation|aerialPhotographyWithContours', 'documentation|urbanAreaSwornStatementAffidavit', 'documentation|miscellaneousDocuments', 'review|specialConditions|twentyFortyAcre', 'review|specialConditions|annualDisposal', 'review|specialConditions|annularDisposal', 'review|specialConditions|ashtabulaDevonianFluidRotary', 'review|specialConditions|ashtabulaDevonianOriskany', 'review|specialConditions|enhancedRecovery', 'review|specialConditions|conductorMineStringComboBasket', 'review|specialConditions|directionalDrilling', 'review|specialConditions|failureToMaintainInsurance', 'review|specialConditions|fluidRotaryLakePlainOriskany', 'review|specialConditions|horizontalDrilling', 'review|specialConditions|hydrogenSulfide', 'review|specialConditions|ielSuperfund', 'review|specialConditions|knoxUnconformity', 'review|specialConditions|lakePlainRegionOriskany', 'review|specialConditions|lakePlainRegionUtica', 'review|specialConditions|mineStringBasketVoid', 'review|specialConditions|mineStringBasketGreaterThreeHundred', 'review|specialConditions|mineStringBasketLessThanThreeHundred', 'review|specialConditions|municipalWellheadProtection', 'review|specialConditions|municipalWellheadProtectionQueenston', 'review|specialConditions|noPerforationZone', 'review|specialConditions|noiseMitigation', 'review|specialConditions|oldWellUnknownStatus', 'review|specialConditions|otherConditions', 'review|specialConditions|prePermittingShaleSite', 'review|specialConditions|prePermittingUrbanSite', 'review|specialConditions|removalOfFluidHorizontalShale', 'review|specialConditions|seismicActivity', 'review|specialConditions|shallowGas', 'review|specialConditions|ssm1', 'review|specialConditions|ssm4', 'review|specialConditions|swiw', 'review|specialConditions|surfaceCasingMineStringCombo', 'review|specialConditions|surfaceCasingMineStringComboBasket', 'review|specialConditions|thinGlacialTillQueenston', 'review|specialConditions|thinGlacialTillClinton', 'review|specialConditions|trempealeau', 'review|specialConditions|wellInAOR'];
        this.dateFields = ['review|completeDate'];
        this.paymentFields = ['payment|payments', 'payments|feesDue'];
        this.authorizationFields = ['payment|creditAuthorization'];
        this.trueBooleanFields = ['permitType|expedite','wellInformation|surfaceGISValidationRun', 'signature|affirmation', 'waterSource|watershedValidated', 'documentation|confidential'];
        this.arrayFields = ['wellInformation|proposedFormation', 'wellInformation|agency'];
    }

    ngOnInit() {
        this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])

        let subs$: Subscription = this.route.data.pipe(
            tap((data: Data) => {
                this.application = data.application;
                this.organization = data.organization;
                this.onBaseDocumentTypes = data.documentTypes;

                this.subheader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber);
                if (this.notesIneligibleStates.includes(this.application.applicationState) == false) {
                    this.subheader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });
                }
                this.subheaderService.updateSubHeader(this.subheader);
			}),
			mergeMap(() => {
				let spec: SystemSettingSearchSpec = {
					settingName: SettingNames.AllowExpedite
				}
				return this._settings.search(spec).pipe(
					tap((settings: SystemSetting[]) => {
						if (settings != null && settings.length > 0) {
							this.expediteSetting = settings.find(s => s.settingName === SettingNames.AllowExpedite);
						}
					})
				)
			}),
            mergeMap(() => {
                return this.domainService.getCasingStrings().pipe(
                    tap((models: CasingTypeModel[]) => {
                        this.casingTypes = [];
                        this.validStringCasingTypeCodes.forEach(c => {
                            let match = models.find(m => m.code === c);
                            if (match != null) this.casingTypes.push(match);
                        });                        
                    })
                )
            }),
            mergeMap(() => {
                return this.domainService.getFormations().pipe(
                    tap((models: FormationModel[]) => {
                        this.formations = models;
                    })
                )
            }),
            mergeMap(() => {
                return this.domainService.getDrillingEquipment().pipe(
                    tap((models: DrillingEquipmentModel[]) => {
                        var select = new DrillingEquipmentModel();
                        select.code = "";
                        select.definition = "";
                        models.unshift(select);
                        this.drillingEquipment = models;
                    })
                )
            }),
            mergeMap(() => {
                return this.domainService.getValues("AGENCY_NAME").pipe(
                    tap((values: any[]) => {
                        this.agencies = values;
                    })
                )
            }),
            mergeMap(() => {
                return this.configService.getModuleConfig().pipe(
                    tap((conf: ModuleConfig) => {
						if (conf != null) {
							if (conf.applications != null) {
								this._config = conf.applications;
								if (this._config.permits != null && this._config.permits.documents != null) this.documentConfig = this._config.permits.documents;
							}
							if (conf.expediteFees != null) {
								this.expediteConfig = conf.expediteFees;
							}
                        }
                    })
                )
            })
            ).subscribe(
            () => {
                    this._loader.toggleLoader(false);
                    if (this.application.organizationId != this.auth.User.organizationId) this.unauthorized();
                    this.buildForm();
                    this.formReady$.next(true);
                    this.updateActiveForm(null, this.form.get('applicationStep').value);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
                this._loader.toggleLoader(false);
            }
        );
        this.subscription.add(subs$);

        
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    
    buildConstructionForm(): FormGroup {
        let obj: any = {};
        this.casingTypes.forEach((type) => {           
            obj[type.code] = this.buildSubConstructionForm(type);
            this.numericFields.push(`construction|${type.code}|boreholeDiameter`);
            this.numericFields.push(`construction|${type.code}|boreholeDepth`);
            this.numericFields.push(`construction|${type.code}|casingDiameter`);
            this.numericFields.push(`construction|${type.code}|casingDepth`);
            this.numericFields.push(`construction|${type.code}|cementTop`);
            this.numericFields.push(`construction|${type.code}|cementSacks`);
            this.numericFields.push(`construction|${type.code}|centralizers`);
        });
        return this.builder.group(obj);
    }

    buildSubConstructionForm(type: CasingTypeModel): FormGroup {
        let subObj: any = {};
        subObj["definition"] = [{ value: type.definition, disabled: true }, []];
        subObj["boreholeDiameter"] = [{ value: null, disabled: false }, []];
        subObj["boreholeDepth"] = [{ value: null, disabled: false }, []];
        subObj["casingDiameter"] = [{ value: null, disabled: false }, []];
        subObj["casingDepth"] = [{ value: null, disabled: false }, []];
        subObj["cementTop"] = [{ value: null, disabled: false }, []];
        subObj["cementSacks"] = [{ value: null, disabled: false }, []];
        subObj["formation"] = [{ value: null, disabled: false }, []];
        subObj["drillingEquipment"] = [{ value: null, disabled: false }, []];
        subObj["centralizers"] = [{ value: null, disabled: false }, []];
        subObj["tested"] = [{ value: null, disabled: false }, []];
        return this.builder.group(subObj);
    }

	buildForm(): void {
		let expediteDefault = (this.expediteSetting != null && this.expediteSetting.isActive) ? null : false;
        let form = this.builder.group({
            applicationStep: [ApplicationSteps.VerifyOwner, []],
            organization: this.builder.group({
                name: [{ value: this.organization.name, disabled: true }],
                address: this.builder.group({
                    street: [{ value: this.organization.address.street, disabled: true }, []],
                    suite: [{ value: this.organization.address.suite, disabled: true }, []],
                    poBox: [{ value: this.organization.address.poBox, disabled: true }, []],
                    city: [{ value: this.organization.address.city, disabled: true }, []],
                    state: [{ value: this.organization.address.stateCode, disabled: true }, []],
                    zip: [{ value: this.organization.address.zipCode, disabled: true }, []]
                }),
                id: [{ value: this.organization.id, disabled: true }],
                phone: [{ value: this.organization.phone, disabled: true }],
                email: [{ value: this.organization.email, disabled: true }]
            }),
            permitType: this.builder.group({
                type: [null, [Validators.required]],
                existingAPI: [{ value: null, disabled: true }, []],
				expedite: [{ value: expediteDefault, disabled: this.expediteSetting == null || this.expediteSetting.isActive == false }, [Validators.required]],
                orientation: [null, [Validators.required]]
            }),
            wellInformation: this.builder.group({
                name: [null, [Validators.required]],
                number: [null, [Validators.required]],
                surfaceLatitude: [null, [Validators.required, Validators.min(0)]],
                surfaceLongitude: [null, [Validators.required, Validators.max(0)]],
                targetLatitude: [{ value: null, disabled: true }],
                targetLongitude: [{ value: null, disabled: true }],
                section: [{ value: "", disabled: true }],
                lot: [{ value: "", disabled: true }],
                tract: [{ value: "", disabled: true }],
                civilTownship: [{ value: "", disabled: true }],
                civilTownshipNumber: [{ value: "", disabled: true }],
                countyName: [{ value: "", disabled: true }],
                countyNumber: [{ value: "", disabled: true }],
                surfaceElevation: [null, [Validators.required]],
                proposedTotalDepth: [null, [Validators.required, Validators.min(0)]],
                proposedFormation: [[], [Validators.required, Validators.minLength(1)]],
                surfaceGISValidationRun: [false, [Validators.requiredTrue]],
                surfaceRights: [null, []],
                agency: [{ value: [], disabled: true }, []]
            }),
            waterSource: this.builder.group({
                source: [null, [Validators.required]],
                latitude: [null, []],
                longitude: [null, []],
                watershed: [null, [Validators.required]],
                watershedOther: [{ value: null, disabled: true }, []],
                withdrawalRate: [null, [Validators.required, Validators.min(0)]],
                totalVolume: [null, [Validators.required, Validators.min(0)]],
                recycledWater: [0, [Validators.required, Validators.min(0)]],
                watershedValidated: [null, []]
            }),
            contacts: this.builder.group({
                fire: this.builder.group({ name: [null, [Validators.required]], phone: [null, [Validators.required, Validators.pattern('[- +()0-9]+')]] }),
                police: this.builder.group({ name: [null, [Validators.required]], phone: [null, [Validators.required, Validators.pattern('[- +()0-9]+')]] }),
                medical: this.builder.group({ name: [null, [Validators.required]], phone: [null, [Validators.required, Validators.pattern('[- +()0-9]+')]] }),
                emaDirector: this.builder.group({ name: [null, []], phone: [null, [Validators.pattern('[- +()0-9]+')]] }),
                planningCommittee: this.builder.group({ name: [null, []], phone: [null, [Validators.pattern('[- +()0-9]+')]] }),
                emergency: this.builder.group({
                    company: [null, [Validators.required]],
                    contact: [null, [Validators.required]],
                    title: [null, []],
                    phone: [null, [Validators.required, Validators.pattern('[- +()0-9]+')]],
                    email: [null, [Validators.email]]
                })
            }),
            access: this.builder.group({
                description: [null, [Validators.required]],
                latitude: [null, [Validators.min(0)]],
                longitude: [null, [Validators.max(0)]]
            }),
            construction: this.buildConstructionForm(),
            documentation: this.builder.group({
                plat: [[], [Validators.required, Validators.minLength(1)]],
                urbanizedArea: [{ value: null, disabled: true }, []],
                aerialPhotography: [[], []],
                aerialPhotographyWithContours: [[], []],
                urbanAreaSwornStatementAffidavit: [[], []],
                miscellaneousDocuments: [[], []],
                confidential: [false, []],
                restorationPlan:[[], [Validators.required, Validators.minLength(1)]]
            }),
            signature: this.builder.group({
                signature: [null, [Validators.required]],
                affirmation: [null, [Validators.requiredTrue]]
            }),
            payment: this.builder.group({
                step: [PaymentSteps.PaymentType, []],
                paymentType: [{ value: null, disabled: true }, [Validators.required]],
                feesDue: [null, []],
                payments: [null, []],
                creditAuthorization: [null, []],
                invoice: [null, [Validators.required]]
            }),
            review: this.builder.group({
                completeDate: [null, []],
                wellClass: ["STRT", []],
                updatedAPI: ["", []],
                specialConditions: this.builder.group({
                    twentyFortyAcre: [[], []],
                    annualDisposal: [[], []],
                    annularDisposal: [[], []],
                    ashtabulaDevonianFluidRotary: [[], []],
                    ashtabulaDevonianOriskany: [[], []],
                    enhancedRecovery: [[], []],
                    conductorMineStringComboBasket: [[], []],
                    directionalDrilling: [[], []],
                    failureToMaintainInsurance: [[], []],
                    fluidRotaryLakePlainOriskany: [[], []],
                    horizontalDrilling: [[], []],
                    hydrogenSulfide: [[], []],
                    ielSuperfund: [[], []],
                    knoxUnconformity: [[], []],
                    lakePlainRegionOriskany: [[], []],
                    lakePlainRegionUtica: [[], []],
                    mineStringBasketVoid: [[], []],
                    mineStringBasketGreaterThreeHundred: [[], []],
                    mineStringBasketLessThanThreeHundred: [[], []],
                    municipalWellheadProtection: [[], []],
                    municipalWellheadProtectionQueenston: [[], []],
                    noPerforationZone: [[], []],
                    noiseMitigation: [[], []],
                    oldWellUnknownStatus: [[], []],
                    otherConditions: [[], []],
                    prePermittingShaleSite: [[], []],
                    prePermittingUrbanSite: [[], []],
                    removalOfFluidHorizontalShale: [[], []],
                    seismicActivity: [[], []],
                    shallowGas: [[], []],
                    ssm1: [[], []],
                    ssm4: [[], []],
                    swiw: [[], []],
                    surfaceCasingMineStringCombo: [[], []],
                    surfaceCasingMineStringComboBasket: [[], []],
                    thinGlacialTillQueenston: [[], []],
                    thinGlacialTillClinton: [[], []],
                    trempealeau: [[], []],
                    wellInAOR: [[], []]
                })
            })
        });
        this.form = this.restoreFormData(form);
       
    }

    confirmSubmit(): void {
        this.form.enable();
        this.form.updateValueAndValidity();
        if (this.form.valid) {
            this.confirmSubmitDialog.message = this.confirmSubmitText.replace("[APPNUMBER]", this.application.applicationNumber);
            this.confirmSubmitDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.confirmSubmitDialog);
        } else {
            this.invalid.toggleDialog(true);
            this.dialogService.updateDialog(this.invalid);
        }
    }

    confirmWithdraw(): void {
        let state: string = this.application.isReturned ? "cancelled" : "withdrawn";
        this.confirmWithdrawDialog.message = this.withdrawDialogText.replace('[APPNUMBER]', this.application.applicationNumber).replace('[STATE]', state);
        this.confirmWithdrawDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmWithdrawDialog);
    }

   

    
    generateApplicationPaymentParameters(forceRecalculate: boolean): ApplicationPaymentParameters {
        return {
            applicationId: this.application.id,
            applicationNumber: this.application.applicationNumber,
            applicationType: ApplicationType.Stratigraphic,
            newWell: this.form.get('permitType').get('existingAPI').value == null,
            permitType: this.form.get('permitType').get('type').value == 'Drill' ? 'New Well' : this.form.get('permitType').get('type').value,
            wellType: 'STRAT',
            wellTypeDescription: '',
            fluidProposed: null,
            forceRecalculate: forceRecalculate,
            expeditePermit: this.form.get('permitType').get('expedite').value == 'true' || this.form.get('permitType').get('expedite').value == true,
            unitMPn: null,
            urbanArea: this.form.get('documentation').get('urbanizedArea').value
        } as ApplicationPaymentParameters;
    }



    getFieldInvalid(formName: string, fieldName: string): boolean {
        if (this.form == null) return false;
        const formToCheck = this.form.get(formName);
        if (formToCheck == null) return false;
        const fieldToCheck = formToCheck.get(fieldName);
        if (fieldToCheck == null) return false;
        return fieldToCheck.invalid;
    }

    home(): void {
        this.allowDeactivate.next(true);
        super.home();
    }

    
    next(): void {
        let currentStep = this.form.get('applicationStep').value;
        let nextStep = currentStep + 1;
        if (nextStep <= 9) {
            this.updateActiveForm(currentStep, nextStep);
            this.form.get('applicationStep').setValue(nextStep);
        }
        else if (nextStep >= 10) {
            nextStep = 0;
            this.updateActiveForm(currentStep, nextStep);
            this.form.get('applicationStep').setValue(nextStep);
        }
    }

    onPermitTypeUpdated(type: string, resetField: boolean): void {
        let field = this.form.get('permitType').get('existingAPI');
        if (type == 'Drill') {
            field.clearValidators();
            if (resetField) field.setValue(null);
            field.disable();
        } else {
            field.setValidators([Validators.required, Validators.minLength(14), Validators.maxLength(14), Validators.pattern(new RegExp("^[0-9]"))]);
            field.enable();
        }
        field.updateValueAndValidity();
        this._applicationPaymentParameters = this.generateApplicationPaymentParameters(true);
    }

    onReceiptIssued(save: boolean): void {
        if (save) {
            this.save(false); 
        }
    }

    orientationChanged(event: any): void {
        let infoForm: FormGroup = this.form.get('wellInformation') as FormGroup;
        if (infoForm == null) return;
        if (event == "Vertical") {
            infoForm.get('targetLatitude').clearValidators();
            infoForm.get('targetLatitude').setValue(null);
            infoForm.get('targetLongitude').clearValidators();
            infoForm.get('targetLongitude').setValue(null);
            
        } else {
            infoForm.get('targetLatitude').setValidators([Validators.required, Validators.min(0)]);
            infoForm.get('targetLongitude').setValidators([Validators.required, Validators.max(0)]);
        }
        infoForm.get('targetLatitude').updateValueAndValidity();
        infoForm.get('targetLongitude').updateValueAndValidity();
    }

    paymentAuthorizationRequested(authorization: AuthorizationRequest): void {
        authorization.accountInformation.paymentType = this.form.get('payment').get('paymentType').value;
        authorization.cancelUrl = `/applications/stratigraphic/input/${this.application.applicationNumber}`;
        authorization.failUrl = `/applications/stratigraphic/input/${this.application.applicationNumber}`;
        authorization.flowUrl = htmlEscape(window.location.href);
        authorization.successUrl = `/applications/stratigraphic/input/${this.application.applicationNumber}`;
        let payments: ApplicationPayment[] = <ApplicationPayment[]>this.form.get('payment').get('payments').value;
        authorization.paymentInformation.amount = 0;
        payments.forEach(p => {
            authorization.paymentInformation.amount += p.fee;
        });
        this.form.get('payment').get('step').setValue(PaymentSteps.Invoicing);
        let obs$: Observable<any> = this.saveProgress().pipe(
            switchMap((saved: WorkflowData) => {
                this.application = saved;
                console.log('save complete');
                this.allowDeactivate.next(true);
                return this.paymentService.sendToCBoss(authorization);
            })
        );
        this._loader.toggleLoader(true);
        let subs$: Subscription = obs$.subscribe(
            (destination) => {
                this._loader.toggleLoader(false);
                if (destination != null) window.location.href = destination;
                else {
                    console.error("no destination received");
                    this.dialogService.showError();
                }
            },
            (error) => {
                console.error(error);
                this._loader.toggleLoader(false);
                this.dialogService.showError();
            }
        );
        this.subscription.add(subs$);
    }

    paymentSaveRequested(payments: ApplicationPayment[]): void {
        if (payments != null && payments.length > 0) {
            let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
                map((update: WorkflowData) => {
                    this.application = update;
                    return this.application;
                })
            );
            this._loader.toggleLoader(true);

            let subs$ = obs$.subscribe(
                (data: WorkflowData) => {
                    let updatedPayments: ApplicationPayment[] = [];
                    let pmtFields = data.workflowFieldDatas.filter(f => this.paymentFields.includes(f.formControlName));
                    let ctrlsToUpdate: FormControl[] = [];
                    pmtFields.forEach(p => {
                        const nameParts = p.formControlName.split('|');
                        let ctrl: FormControl;
                        switch (nameParts.length) {
                            case 1:
                                ctrl = this.form.get(nameParts[0]) as FormControl;
                                break;
                            case 2:
                                ctrl = this.form.get(nameParts[0]).get(nameParts[1]) as FormControl;
                                break;
                            case 3:
                                ctrl = this.form.get(nameParts[0]).get(nameParts[1]).get(nameParts[2]) as FormControl;
                                break;
                        }
                        if (p.applicationPayments != null) {
                            p.applicationPayments.forEach(pmt => {
                                updatedPayments.push(pmt);
                            });
                        }
                        if (ctrl != null) ctrlsToUpdate.push(ctrl);
                    })
                    this._loader.toggleLoader(false);
                    ctrlsToUpdate.forEach(ctrl => { ctrl.setValue(updatedPayments); });
                    this.form.get('payment').get('step').setValue(PaymentSteps.Invoicing);
                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }
            );
            this.subscription.add(subs$);
        }       
    }

  

    previous(): void {
        let currentStep = this.form.get('applicationStep').value;
        let nextStep = currentStep - 1;
        if (nextStep >= 0) {
            this.updateActiveForm(currentStep, nextStep);
            this.form.get('applicationStep').setValue(nextStep);
        } 
    }

   

    setAuthorizationRecordIds(): void {
      
    }

    resetInvalid(): void {
        this.form.disable();
        this.updateActiveForm(this.form.get('applicationStep').value, this.form.get('applicationStep').value);
    }

    stepChanged(nextStep: ApplicationSteps): void {
        this.updateActiveForm(this.form.get('applicationStep').value, nextStep);
        this.form.get('applicationStep').setValue(nextStep);

    }
    
   

    submit(): void {
        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.InitialReview;
                if (this.application.isReturned == false || (this.application.workflowMetadata != null && this.application.workflowMetadata.stratigraphicApplicationMetadata != null && this.application.workflowMetadata.stratigraphicApplicationMetadata.applicationCompleteDate == null)) {
                    destination = ApplicationState.VerifyCompletion;
                }
                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.submittedDialog.message = this.submittedDialog.message.replace('[APPNUMBER]', app.applicationNumber);
                    this._loader.toggleLoader(false);
                    this.submittedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.submittedDialog);
                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }
            );
        this.subscription.add(subs$);
    }

    surfaceRightsChanged(value: boolean | string): void {
        if (value == true || value == "true") {
            this.form.get("wellInformation").get("agency").enable();
            this.form.get("wellInformation").get("agency").setValidators([Validators.required, Validators.minLength(1)]);
        }
        else {
            this.form.get("wellInformation").get("agency").disable();
            this.form.get("wellInformation").get("agency").setValue([]);
            this.form.get("wellInformation").get("agency").clearValidators();
        }
        this.form.get("wellInformation").get("agency").updateValueAndValidity();
    }

    updateActiveForm(currentStep: number, nextStep: number): void {
        switch (currentStep) {
            case ApplicationSteps.PermitType: {
                this.form.get('permitType').disable();
                break;
            }
            case ApplicationSteps.WellInformation: {
                this.form.get('wellInformation').disable();
                break;
            }
            case ApplicationSteps.WaterSource: {
                this.form.get("waterSource").disable();
                break;
            }
            case ApplicationSteps.Contacts: {
                this.form.get("contacts").disable();
                break;
            }
            case ApplicationSteps.WellSiteAccess: {
                this.form.get("access").disable();
                break;
            }
            case ApplicationSteps.WellConstruction: {
                this.form.get("construction").disable();
                break;
            }
            case ApplicationSteps.Documentation: {
                this.form.get("documentation").disable();
                break;
            }
            case ApplicationSteps.Signature: {
                this.form.get("signature").disable();
                break;
            }
            case ApplicationSteps.Payment: {
                this.form.get("payment").disable();
                break;
            }
            default: {
                this.form.get('permitType').disable();
                this.form.get('wellInformation').disable();
                this.form.get("waterSource").disable();
                this.form.get("contacts").disable();
                this.form.get("access").disable();
                this.form.get("construction").disable();
                this.form.get("documentation").disable();
                this.form.get("signature").disable();
                this.form.get("payment").disable();
                break;
            }

        }
        let infoForm: FormGroup;
        let formName: string = "";
        switch (nextStep) {
            case ApplicationSteps.VerifyOwner:
                formName = 'organization';
                infoForm = <FormGroup>this.form.get(formName);
                break;
            case ApplicationSteps.PermitType: {
                formName = 'permitType';
                infoForm = <FormGroup>this.form.get(formName);
				infoForm.enable();
				if (this.expediteSetting == null || this.expediteSetting.isActive === false) {
					let ctrl = infoForm.get('expedite');
					if (ctrl != null) {
						ctrl.disable(); //keep the expedite control disabled if expedites aren't being accepted
					}
				}
                let selectedType = infoForm.get('type').value;
                this.onPermitTypeUpdated(selectedType, false);
                break;
            }
            case ApplicationSteps.WellInformation: {
                formName = 'wellInformation';
                infoForm = <FormGroup>this.form.get(formName);
                infoForm.enable();
                const orientationField = this.form.get('permitType').get('orientation')
                let orientation = orientationField.value;
                if (orientation == null || orientation == 'Vertical') {
                    infoForm.get("targetLatitude").disable();
                    infoForm.get("targetLongitude").disable();
                } if (infoForm == null) return;
                if (orientation == null || orientation == "Vertical") {
                    infoForm.get('targetLatitude').clearValidators();
                    infoForm.get('targetLongitude').clearValidators();
                    infoForm.get('targetLatitude').disable();
                    infoForm.get('targetLongitude').disable();
                } else {
                    infoForm.get('targetLatitude').setValidators([Validators.required, Validators.min(0)]);
                    infoForm.get('targetLongitude').setValidators([Validators.required, Validators.max(0)]);                    
                }
                infoForm.get('targetLatitude').updateValueAndValidity();
                infoForm.get('targetLongitude').updateValueAndValidity();
                infoForm.get('section').disable();
                infoForm.get("lot").disable();
                infoForm.get("tract").disable();
                infoForm.get("civilTownship").disable();
                infoForm.get("countyName").disable();
                infoForm.get("countyNumber").disable();
                if (infoForm.get("surfaceRights").value == null || infoForm.get("surfaceRights").value == false) infoForm.get("agency").disable();
                else this.surfaceRightsChanged(infoForm.get("surfaceRights").value);
                break;
            }
            case ApplicationSteps.WaterSource: {
                formName = 'waterSource';
                infoForm = <FormGroup>this.form.get(formName);
                infoForm.enable();
                this.watershedChanged(infoForm.get("watershed").value);
                this.watershedCoordinatesChanged(null);
                infoForm.updateValueAndValidity();
                break;
            }
            case ApplicationSteps.Contacts: {
                formName = 'contacts';
                infoForm = <FormGroup>this.form.get(formName);
                infoForm.enable();
                break;
            }
            case ApplicationSteps.WellSiteAccess: {
                formName = 'access';
                infoForm = <FormGroup>this.form.get(formName);
                infoForm.enable();
                break;
            }
            case ApplicationSteps.WellConstruction: {
                formName = 'construction';
                infoForm = <FormGroup>this.form.get(formName);
                infoForm.enable();
                break;
            }
            case ApplicationSteps.Documentation: {
                formName = 'documentation';
                infoForm = <FormGroup>this.form.get(formName);
                infoForm.enable();
                this.urbanizedAreaChanged(infoForm.get("urbanizedArea").value, false);
                infoForm.get("urbanizedArea").disable();
                infoForm.updateValueAndValidity();
                break;
            }
            case ApplicationSteps.Signature: {
                formName = 'signature';
                infoForm = <FormGroup>this.form.get(formName);
                infoForm.enable();
                break;
            }
            case ApplicationSteps.Payment: {
                formName = 'payment';
                infoForm = <FormGroup>this.form.get(formName);
                this._applicationPaymentParameters = this.generateApplicationPaymentParameters(this._applicationPaymentParameters != null && this._applicationPaymentParameters.forceRecalculate);
                infoForm.enable();
                break;
            }
            case ApplicationSteps.Review: {
                formName = 'review';
                infoForm = <FormGroup>this.form.get(formName);
                break;
            }
        }
        if (infoForm != null) {            
            infoForm.updateValueAndValidity();
            if (this.application.applicationState == ApplicationState.Corrections) {
                if (this.alwaysDisabledFormNames.includes(formName)) infoForm.disable();
                else {
                    let fields = this.application.workflowFieldDatas.filter(f => f.formControlName.startsWith(formName));
                    fields.forEach(f => {
                        let ctrl = this.findMatchingControl(this.form, f.formControlName);
                        if (ctrl != null) {
                            if (this.alwaysDisabledFieldNames.includes(f.formControlName) || this.fieldHasNotes(f.id) == false) {
                                ctrl.disable();
                            }
                        }
                    });
                }
            }
        }       
    }

    updateResponse(response: AuthorizationResponse): void {
        let auth: ApplicationCreditAuthorization = <ApplicationCreditAuthorization>response;
        let field = this.application.workflowFieldDatas.find(f => f.formControlName == 'payment|creditAuthorization');
        if (field != null) auth.id = field.id;
        if (response.Error == "False") {
            if (this.form != null) {
                let ctrl = this.form.get('payment').get('creditAuthorization');
                if (ctrl != null) {
                    ctrl.setValue(auth);
                }
            }
            let unallocated = parseFloat(response.Amount);
            let feesWithPayments: ApplicationPayment[] = [];
            if (isNaN(unallocated) == false) {
                for (let p = 0; p < this.fees.length; p++) {
                    let fee: ApplicationPayment = this.fees[p];
                    if (fee.amountPaid == null) fee.amountPaid = 0;
                    if (unallocated > 0) {
                        fee.amountDue = fee.fee - fee.amountPaid;
                        if (fee.amountDue <= unallocated) {
                            fee.amountPaid += fee.amountDue;
                            unallocated -= fee.amountDue;
                            let record: Payment = {
                                applicationPaymentId: fee.id,
                                amount: fee.amountDue,
                                instrumentAmount: parseFloat(response.Amount),
                                paymentType: response.PaymentType,
                                paymentDate: new Date(),
                                token: null,
                                tokenId: response.Token,
                                paymentReceivedDate: new Date(),
                                paymentReceivedById: "System",
                                applicationCreditAuthorizations: [
                                    auth
                                ]
                            } as Payment;
                            fee.amountDue -= fee.amountPaid;
                            if (fee.paymentRecords == null) fee.paymentRecords = [];
                            fee.paymentRecords.push(record);
                        } else if (fee.amountDue > unallocated) {
                            fee.amountPaid += unallocated;
                            let record: Payment = {
                                applicationPaymentId: fee.id,
                                amount: unallocated,
                                instrumentAmount: parseFloat(response.Amount),
                                paymentType: response.PaymentType,
                                paymentDate: new Date(),
                                /*       token: response.Token,*/
                                tokenId: response.Token,
                                paymentReceivedDate: new Date(),
                                paymentReceivedById: "System",
                                authorizationId: auth.id
                            } as Payment;
                            fee.amountDue -= unallocated;
                            unallocated = 0;
                            if (fee.paymentRecords == null) fee.paymentRecords = [];
                            fee.paymentRecords.push(record);
                        }
                        
                        feesWithPayments.push(fee);
                    } else {
                        
                        feesWithPayments.push(fee);
                    }
                }
                this.fees = feesWithPayments;
                
            }
        }
    }

    urbanizedAreaChanged(value: string, setValue: boolean = true): void {
        if (setValue) {
            if (this.form != null) {
                let docCtrl = this.form.get('documentation');
                if (docCtrl != null) {
                    let ctrl = docCtrl.get('urbanizedArea');
                    if (ctrl != null) {
                        ctrl.setValue(value);
                        ctrl.updateValueAndValidity();
                    }
                }
            }
        }
        if (this.aerialPhotographyCtrl != null && this.aerialPhotographyWithContoursCtrl != null && this.urbanAreaCtrl != null) {
            if (value != "NONURB") {
                this.aerialPhotographyCtrl.setValidators([Validators.required, Validators.minLength(1)]);
                this.aerialPhotographyWithContoursCtrl.setValidators([Validators.required, Validators.minLength(1)]);
                this.urbanAreaCtrl.setValidators([Validators.required, Validators.minLength(1)]);
            } else {
                this.aerialPhotographyCtrl.clearValidators();
                this.aerialPhotographyWithContoursCtrl.clearValidators();
                this.urbanAreaCtrl.clearValidators();
            }
            this.aerialPhotographyCtrl.updateValueAndValidity();
            this.aerialPhotographyWithContoursCtrl.updateValueAndValidity();
            this.urbanAreaCtrl.updateValueAndValidity();
        }        
    }

    watershedCoordinatesChanged(value: number): void {
        let validCtrl = this.form.get('waterSource').get('watershedValidated');
        let shedCtrl = this.form.get('waterSource').get('watershed');
        if (validCtrl == null || shedCtrl == null) return;
        if (this.requireWatershedValidation) {
            if (validCtrl.invalid || validCtrl.value == null) {
                validCtrl.setValue(false);
                validCtrl.setValidators([Validators.requiredTrue]);
                shedCtrl.setValue(null);
            }
            shedCtrl.disable();
           
        } else {
            validCtrl.setValue(null);
            validCtrl.clearValidators();
        }
        validCtrl.updateValueAndValidity();
        shedCtrl.updateValueAndValidity();
    }
      
    watershedChanged(value: string): void {
        let sourceForm = this.form.get('waterSource');
        if (sourceForm != null) {
            let otherCtrl = sourceForm.get("watershedOther");
            if (otherCtrl != null) {
                if (value == "OTHER") {
                    otherCtrl.enable();
                    otherCtrl.setValidators([Validators.required]);
                } else {
                    otherCtrl.setValue(null);
                    otherCtrl.clearValidators();
                    otherCtrl.disable();
                }
                otherCtrl.updateValueAndValidity()
            }           
        }
    }

    withdraw(): void {
        let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: ApplicationState.Withdrawn
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.withdrawDialog.message = this.withdrawDialog.message.replace('[APPNUMBER]', app.applicationNumber);
                    let state: string = app.isReturned ? "cancelled" : "withdrawn";
                    this.withdrawDialog.message = this.withdrawDialog.message.replace('[STATE]', state);
                    this._loader.toggleLoader(false);
                    this.withdrawDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.withdrawDialog);
                },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                    this._loader.toggleLoader(false);
                }
        );
        this.subscription.add(subs$);

    }
}
